/*
 * snapped-product.js
 * Minimal behaviour for the custom Snapped product-page sections.
 * Everything that Dawn already does natively (variant switching, AJAX add-to-cart,
 * quantity rules) is left to Dawn — this file only adds: the custom gallery,
 * the tabbed content, the sticky add-to-cart bar, and the custom "Buy It Now".
 *
 * All initialisers are idempotent (guarded with a data flag) and re-run on
 * `shopify:section:load` so they keep working inside the theme editor.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- Gallery */
  function initGallery(root) {
    root.querySelectorAll('[data-cpm-gallery]').forEach(function (gallery) {
      if (gallery.dataset.cpmBound) return;
      gallery.dataset.cpmBound = 'true';

      var mainImg = gallery.querySelector('[data-cpm-main-img]');
      if (!mainImg) return;
      var thumbs = gallery.querySelectorAll('[data-cpm-thumb]');

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var full = thumb.getAttribute('data-full');
          if (!full) return;
          thumbs.forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
          mainImg.style.opacity = '0';
          window.setTimeout(function () {
            // The main <img> carries a srcset; when present the browser picks from it and IGNORES
            // src — so we must swap srcset too, otherwise the displayed image never changes.
            var ss = thumb.getAttribute('data-srcset');
            if (ss) { mainImg.srcset = ss; } else { mainImg.removeAttribute('srcset'); }
            mainImg.src = full;
            var inner = thumb.querySelector('img');
            if (inner) mainImg.alt = inner.alt;
            mainImg.style.opacity = '1';
          }, 180);
        });
      });

      // Lightbox zoom — built without native dialog/alert (which would block the page).
      var zoom = gallery.querySelector('[data-cpm-zoom]');
      if (zoom) {
        zoom.addEventListener('click', function () {
          var overlay = document.createElement('div');
          overlay.className = 'cpm-lightbox';
          overlay.setAttribute('role', 'dialog');
          overlay.setAttribute('aria-modal', 'true');
          overlay.innerHTML =
            '<button class="cpm-lightbox__close" aria-label="Close">&times;</button>' +
            '<img class="cpm-lightbox__img" src="' + mainImg.src + '" alt="' + (mainImg.alt || '') + '">';
          function close() {
            overlay.remove();
            document.removeEventListener('keydown', onKey);
          }
          function onKey(e) { if (e.key === 'Escape') close(); }
          overlay.addEventListener('click', function (e) {
            if (e.target === overlay || e.target.classList.contains('cpm-lightbox__close')) close();
          });
          document.addEventListener('keydown', onKey);
          document.body.appendChild(overlay);
        });
      }
    });
  }

  /* Tabs: intentionally none — the tabs section switches via CSS-only radio
     inputs (:checked), so no tab JavaScript is authored here. */

  /* -------------------------------------------------------- Sticky ATC bar */
  function initStickyBar(root) {
    root.querySelectorAll('[data-cpm-main]').forEach(function (section) {
      if (section.dataset.cpmStickyBound) return;
      section.dataset.cpmStickyBound = 'true';

      var bar = section.querySelector('[data-cpm-stickybar]');
      var atcArea = section.querySelector('[data-cpm-atc]');
      var addBtn = section.querySelector('product-form [name="add"]');
      if (!bar || !atcArea) return;

      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            // Show the bar only once the main ATC has scrolled out of view upward.
            var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            bar.classList.toggle('is-visible', show);
            if (show) { bar.removeAttribute('aria-hidden'); } else { bar.setAttribute('aria-hidden', 'true'); }
          });
        }, { threshold: 0 });
        observer.observe(atcArea);
      }

      var stickyAdd = bar.querySelector('[data-cpm-sticky-add]');
      if (stickyAdd && addBtn) {
        stickyAdd.addEventListener('click', function () {
          if (addBtn.hasAttribute('disabled')) return;
          addBtn.click(); // let Dawn's product-form handle the AJAX add + cart drawer
        });
      }

      // Instant feedback for the selected-option label (e.g. "Length — 16").
      var radios = section.querySelector('variant-radios');
      if (radios) {
        radios.addEventListener('change', function () { updateSelectedLabels(section); });
      }

      // Keep the sticky bar's price/length + option label in sync with Dawn's variant change.
      // PUB_SUB_EVENTS is a bare global const (constants.js), NOT a window property.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, function () {
          syncSticky(section, bar);
          updateSelectedLabels(section);
        });
      }
      syncSticky(section, bar);
      updateSelectedLabels(section);
    });
  }

  function updateSelectedLabels(section) {
    section.querySelectorAll('[data-cpm-variant-fieldset]').forEach(function (fs) {
      var checked = fs.querySelector('input:checked');
      var out = fs.querySelector('[data-cpm-selected-option]');
      if (checked && out) out.textContent = checked.value;
    });
  }

  function syncSticky(section, bar) {
    var priceEl = section.querySelector('[id^="price-"] .cpm-price__current');
    var priceOut = bar.querySelector('[data-cpm-sticky-price]');
    var checked = section.querySelector('variant-radios input:checked');
    if (priceOut) {
      var txt = priceEl ? priceEl.textContent.trim() : '';
      if (checked) txt += (txt ? ' · ' : '') + checked.value;
      priceOut.textContent = txt;
    }
  }

  /* ------------------------------------------------------ Custom Buy It Now */
  function initBuyNow(root) {
    root.querySelectorAll('[data-cpm-buynow]').forEach(function (btn) {
      if (btn.dataset.cpmBound) return;
      btn.dataset.cpmBound = 'true';

      btn.addEventListener('click', function () {
        var section = btn.closest('[data-cpm-main]');
        if (!section) return;
        var addBtn = section.querySelector('product-form [name="add"]');
        if (addBtn && addBtn.hasAttribute('disabled')) return;

        var variantInput = section.querySelector('.product-variant-id');
        var qtyInput = section.querySelector('.quantity__input');
        var id = variantInput && variantInput.value;
        if (!id) return;
        var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        btn.classList.add('is-loading');
        btn.setAttribute('disabled', 'disabled');

        fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: id, quantity: quantity }] }),
        })
          .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
          .then(function (res) {
            if (!res.ok) throw new Error(res.data && res.data.description ? res.data.description : 'Add to cart failed');
            window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'checkout';
          })
          .catch(function (e) {
            console.error('[snapped] Buy It Now:', e);
            btn.classList.remove('is-loading');
            btn.removeAttribute('disabled');
          });
      });
    });
  }

  /* ----------------------------------------------- Quantity typed-value clamp */
  /* Dawn's <quantity-input> clamps the +/- buttons to the input's max but does NOT
     clamp a value the user TYPES. This adds only that clamp on top of Dawn's
     component (it does not replace it). Delegated on document so it keeps working
     after Dawn re-renders the section on a variant change; bound once. */
  function initQtyClamp() {
    if (window.__cpmQtyClampBound) return;
    window.__cpmQtyClampBound = true;
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || !input.classList || !input.classList.contains('quantity__input')) return;
      if (!input.closest('.cpm-qty') || input.dataset.cpmClamping) return;
      var max = input.max === '' ? null : parseInt(input.max, 10);
      var min = input.min === '' ? null : parseInt(input.min, 10);
      var val = parseInt(input.value, 10);
      if (isNaN(val)) val = min == null ? 1 : min;
      if (max != null && !isNaN(max) && val > max) val = max;
      if (min != null && !isNaN(min) && val < min) val = min;
      if (String(val) !== input.value) {
        input.dataset.cpmClamping = '1';
        input.value = val;
        // Re-fire change so Dawn's quantity-input re-validates the +/- disabled states.
        input.dispatchEvent(new Event('change', { bubbles: true }));
        delete input.dataset.cpmClamping;
      }
    });
  }

  /* Keep the inventory cap correct across variant switches.
     Dawn's product-info.updateQuantityRules() copies data-max from the freshly-rendered
     variant but NEVER removes a stale one, so switching from a stock-capped (deny) variant
     to a continue-selling / untracked one (which has no data-max) leaves a phantom cap.
     We reconcile only that case, reusing the HTML Dawn already fetched (event.data.html)
     and then re-running Dawn's own setQuantityBoundries — no extra request, no new qty logic. */
  function initQtyMaxSync() {
    if (window.__cpmQtyMaxSyncBound) return;
    // PUB_SUB_EVENTS is a bare global const (constants.js), NOT a window property.
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) return;
    window.__cpmQtyMaxSyncBound = true;
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      if (!event || !event.data || !event.data.html || !event.data.sectionId) return;
      var sectionId = event.data.sectionId;
      var freshForm = event.data.html.getElementById('Quantity-Form-' + sectionId);
      var liveInput = document.querySelector('#Quantity-Form-' + sectionId + ' .quantity__input');
      if (!freshForm || !liveInput) return;
      var freshInput = freshForm.querySelector('.quantity__input');
      var freshHasCap = freshInput && freshInput.getAttribute('data-max') !== null;
      // Defer via microtask so this runs AFTER Dawn's synchronous variantChange handler
      // (updateQuantityRules + setQuantityBoundries). A microtask fires reliably even in a
      // background tab, where requestAnimationFrame would be paused.
      Promise.resolve().then(function () {
        if (!freshHasCap && liveInput.hasAttribute('data-max')) {
          liveInput.removeAttribute('data-max');
          var pi = liveInput.closest('product-info');
          if (pi && typeof pi.setQuantityBoundries === 'function') pi.setQuantityBoundries();
          liveInput.removeAttribute('max');
        }
      });
    });
  }

  /* -------------------------------------------------------------- Bootstrap */
  function initAll(root) {
    var scope = root || document;
    initGallery(scope);
    initStickyBar(scope);
    initBuyNow(scope);
    initQtyClamp();
    initQtyMaxSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
